/**
 * A phone number, formatted the way somebody would write it down.
 *
 * One function, shared, because a number typed on the contact form, one
 * imported from a spreadsheet and one read back on a visit have to look the
 * same — and because "(717) 555-0142", "717-555-0142" and "7175550142" are
 * the same number and nothing downstream should have to know that.
 *
 * The shape is North American: ten digits as `(717) 555-0142`, and anything
 * after them as an extension, `(717) 555-0142 x204`. Digits are all that
 * survive — letters and punctuation are dropped as they are typed, so a
 * pasted "Tel: 717.555.0142 ext 204" lands correctly rather than being
 * rejected.
 *
 * A number shorter than ten digits wears the shape of the first few of ten,
 * because that is what somebody halfway through typing one is looking at. A
 * leading US country code — "1" in front of ten digits — is dropped, because
 * it is the same number.
 */
/** Every digit in what was typed, and nothing else. */
export const phoneDigits = (input) => input.replace(/\D+/g, '');
/**
 * What the box shows while it is being typed into.
 *
 * Formats as far as the digits go and no further: three digits give
 * "(717", six give "(717) 555", ten give the whole thing. That is what makes
 * it live — the brackets and the dash arrive under the caret as the digits
 * that need them are typed, rather than on blur, when it is too late to have
 * been any help.
 */
export function formatPhone(input) {
    let digits = phoneDigits(input);
    /* A leading US country code is the same number with a 1 in front of it. */
    if (digits.length > 10 && digits.startsWith('1'))
        digits = digits.slice(1);
    const ten = digits.slice(0, 10);
    const extension = digits.slice(10);
    let shown;
    if (ten.length === 0)
        shown = '';
    else if (ten.length <= 3)
        shown = `(${ten}`;
    else if (ten.length <= 6)
        shown = `(${ten.slice(0, 3)}) ${ten.slice(3)}`;
    else
        shown = `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
    return extension ? `${shown} x${extension}` : shown;
}
/**
 * The same number with nothing pretty about it, for storing and comparing.
 *
 * `+17175550142` for a full number, so two records of the same person match
 * whichever way each was typed; an extension is kept behind an `x` because it
 * is part of reaching the person and dropping it would send somebody to a
 * switchboard.
 */
export function normalizePhone(input) {
    let digits = phoneDigits(input);
    if (digits.length === 0)
        return null;
    if (digits.length > 10 && digits.startsWith('1'))
        digits = digits.slice(1);
    const ten = digits.slice(0, 10);
    const extension = digits.slice(10);
    if (ten.length !== 10)
        return digits;
    return extension ? `+1${ten}x${extension}` : `+1${ten}`;
}
//# sourceMappingURL=phone.js.map